// /app/frontend/src/components/admin/CompanyUsersTab.jsx
// User Management with Roles, Teams & Activity Logs

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { DeleteUserDialog } from './users/DeleteUserDialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import {
  Users, UserPlus, Shield, UsersRound, Activity,
  Search, MoreVertical, Check, X, Mail, Clock,
  Building2, Pencil, Trash2, UserCheck, UserX, Eye,
  Upload, Download, ShieldCheck, QrCode, Send, AlertCircle, RefreshCw, Bell, BellOff, Star, MessageSquare
} from 'lucide-react';
import { PermissionGate } from '../../context/PermissionContext';
import { UserSecurityDialog } from './users/UserSecurityDialog';
import { BackupCodesDialog, BackupCodesPanel } from './users/BackupCodes';
import { KeyRound } from 'lucide-react';
import usePushNotifications from '../../hooks/usePushNotifications';
import { sanitizeHtml } from '../../lib/sanitize';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

// Push Notification Settings Component
function PushNotificationSettings() {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe
  } = usePushNotifications();

  const token = localStorage.getItem('apollo_token');
  const headers = { Authorization: `Bearer ${token}` };

  const handleToggle = async () => {
    try {
      if (isSubscribed) {
        await unsubscribe();
        toast.success('Push notifications disabled');
      } else {
        await subscribe();
        toast.success('Push notifications enabled!');
      }
    } catch (error) {
      if (error.message === 'Permission denied') {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error('Failed to update notification settings');
      }
    }
  };

  const handleTestNotification = async () => {
    try {
      await axios.post(`${API}/api/push/test`, {}, { headers });
      toast.success('Test notification sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test notification');
    }
  };

  if (!isSupported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="h-5 w-5" />
            Push Notifications
          </CardTitle>
          <CardDescription>Real-time alerts for schedule changes and assignments</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-gray-50 rounded-lg text-gray-500">
            Push notifications are not supported in this browser.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Push Notifications
        </CardTitle>
        <CardDescription>Get real-time alerts for new assignments, schedule changes, and reminders</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium">Notification Status</p>
            <p className="text-sm text-gray-500">
              {isSubscribed 
                ? 'Push notifications are enabled for this device' 
                : permission === 'denied' 
                  ? 'Notifications blocked - enable in browser settings'
                  : 'Enable to receive real-time updates'
              }
            </p>
          </div>
          {isSubscribed ? (
            <Badge className="bg-green-600 text-white">Enabled</Badge>
          ) : permission === 'denied' ? (
            <Badge className="bg-red-500 text-white">Blocked</Badge>
          ) : (
            <Badge className="bg-gray-400 text-white">Disabled</Badge>
          )}
        </div>
        
        <div className="flex gap-2 mt-4">
          <Button 
            onClick={handleToggle} 
            disabled={loading || permission === 'denied'}
            variant={isSubscribed ? 'outline' : 'default'}
            data-testid="toggle-push-notifications"
          >
            {loading ? 'Loading...' : isSubscribed ? (
              <>
                <BellOff className="h-4 w-4 mr-2" />
                Disable Notifications
              </>
            ) : (
              <>
                <Bell className="h-4 w-4 mr-2" />
                Enable Notifications
              </>
            )}
          </Button>
          
          {isSubscribed && (
            <Button variant="outline" onClick={handleTestNotification}>
              <Send className="h-4 w-4 mr-2" />
              Send Test
            </Button>
          )}
        </div>

        <div className="mt-4 pt-4 border-t text-sm text-gray-500">
          <p className="font-medium text-gray-700 mb-2">You'll be notified about:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>New job assignments</li>
            <li>Schedule changes and updates</li>
            <li>Appointment reminders</li>
            <li>Important system alerts</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

// Role badge colors
const ROLE_COLORS = {
  'role_super_admin': 'bg-purple-600 text-white',
  'role_admin': 'bg-blue-600 text-white',
  'role_sales_manager': 'bg-emerald-600 text-white',
  'role_sales_rep': 'bg-emerald-500 text-white',
  'role_production_manager': 'bg-orange-600 text-white',
  'role_installer': 'bg-amber-600 text-white',
  'role_accounting': 'bg-cyan-600 text-white',
  'role_customer_service': 'bg-pink-600 text-white',
  'role_trade_partner': 'bg-indigo-600 text-white',
};

// Permission level badges
const LEVEL_BADGES = {
  'full': 'bg-green-600 text-white',
  'own': 'bg-blue-500 text-white',
  'edit': 'bg-yellow-500 text-black',
  'view': 'bg-gray-500 text-white',
  'none': 'bg-gray-300 text-gray-600',
  'sales': 'bg-emerald-500 text-white',
  'production': 'bg-orange-500 text-white',
};

export default function CompanyUsersTab() {
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role_id === 'role_super_admin';
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [permissionModules, setPermissionModules] = useState([]);
  const [permissionLevels, setPermissionLevels] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Modals
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [securityUser, setSecurityUser] = useState(null);
  const [newBackupCodes, setNewBackupCodes] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);

  // Bulk import
  const [bulkImportData, setBulkImportData] = useState([]);
  const [bulkImportResults, setBulkImportResults] = useState(null);
  const fileInputRef = useRef(null);

  // 2FA
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showDisable2FAModal, setShowDisable2FAModal] = useState(false);
  const [disable2FAForm, setDisable2FAForm] = useState({ password: '', code: '' });

  // Email logs
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsSummary, setEmailLogsSummary] = useState({ total: 0, successful: 0, failed: 0 });
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [activitySection, setActivitySection] = useState('logs');
  
  // Customer feedback
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState({ total: 0, completed: 0, pending: 0, average_rating: 0 });
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  // Email preview modal
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreviewContent, setEmailPreviewContent] = useState(null);

  // Invite form
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role_id: '',
    team_id: ''
  });

  // Role form
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: {}
  });

  // Team form
  const [teamForm, setTeamForm] = useState({
    name: '',
    description: '',
    manager_id: ''
  });

  const token = localStorage.getItem('apollo_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, [activeSubTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, teamsRes, modulesRes] = await Promise.all([
        axios.get(`${API}/api/admin/company-users`, { headers }),
        axios.get(`${API}/api/admin/roles`, { headers }),
        axios.get(`${API}/api/admin/teams`, { headers }),
        axios.get(`${API}/api/admin/roles/modules/list`, { headers })
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setTeams(teamsRes.data);
      setPermissionModules(modulesRes.data.modules || []);
      setPermissionLevels(modulesRes.data.levels || []);

      if (activeSubTab === 'activity') {
        const logsRes = await axios.get(`${API}/api/admin/activity-logs?limit=100`, { headers });
        setActivityLogs(logsRes.data);
        // Also fetch email logs
        fetchEmailLogs();
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch email logs
  const fetchEmailLogs = async () => {
    setEmailLogsLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/email-logs?limit=100`, { headers });
      setEmailLogs(response.data.logs || []);
      setEmailLogsSummary(response.data.summary || { total: 0, successful: 0, failed: 0 });
    } catch (error) {
      console.error('Failed to fetch email logs:', error);
    } finally {
      setEmailLogsLoading(false);
    }
  };

  // Fetch customer feedback
  const fetchFeedback = async () => {
    setFeedbackLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/feedback`, { headers });
      setFeedbackList(response.data.feedback || []);
      setFeedbackStats(response.data.stats || { total: 0, completed: 0, pending: 0, average_rating: 0 });
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Retry failed email
  const handleRetryEmail = async (logId, toEmail) => {
    try {
      const response = await axios.post(`${API}/api/admin/email-logs/${logId}/retry`, {}, { headers });
      if (response.data.success) {
        toast.success(`Email retry sent to ${toEmail}`);
      } else {
        toast.error('Email retry failed');
      }
      fetchEmailLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry email');
    }
  };

  // Preview email content
  const previewEmail = async (logId) => {
    try {
      const response = await axios.get(`${API}/api/admin/email-logs/${logId}/preview`, { headers });
      setEmailPreviewContent(response.data);
      setShowEmailPreview(true);
    } catch (error) {
      toast.error('Failed to load email preview');
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role_id === filterRole;
    const matchesTeam = filterTeam === 'all' || user.team_id === filterTeam;
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && user.is_active) ||
      (filterStatus === 'inactive' && !user.is_active && !user.invite_pending) ||
      (filterStatus === 'pending' && user.invite_pending);
    return matchesSearch && matchesRole && matchesTeam && matchesStatus;
  });

  // Invite user
  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.name || !inviteForm.role_id) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await axios.post(`${API}/api/admin/company-users/invite`, inviteForm, { headers });
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setShowInviteModal(false);
      setInviteForm({ email: '', name: '', role_id: '', team_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  // Update user role/team
  const handleUpdateUserRole = async (userId, roleId, teamId) => {
    try {
      await axios.put(`${API}/api/admin/company-users/${userId}/role`, {
        role_id: roleId,
        team_id: teamId
      }, { headers });
      toast.success('User updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  // Activate/Deactivate user
  const handleToggleUserStatus = async (userId, activate) => {
    try {
      const endpoint = activate ? 'activate' : 'deactivate';
      await axios.post(`${API}/api/admin/company-users/${userId}/${endpoint}`, {}, { headers });
      toast.success(`User ${activate ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  // Delete user (pending invite or active user)
  const handleDeleteUser = async (userId, userName) => {
    const confirmMsg = `Are you sure you want to delete "${userName}"? This action cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.delete(`${API}/api/admin/company-users/${userId}`, { headers });
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Resend invitation
  const handleResendInvite = async (userId, email) => {
    try {
      await axios.post(`${API}/api/admin/company-users/${userId}/resend-invite`, {}, { headers });
      toast.success(`Invitation resent to ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  // Bulk CSV Import
  const handleCSVUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      const users = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const user = {};
        headers.forEach((header, idx) => {
          if (header === 'email' || header === 'name') {
            user[header] = values[idx] || '';
          }
        });
        if (user.email && user.name) {
          users.push(user);
        }
      }
      
      setBulkImportData(users);
      toast.success(`Parsed ${users.length} users from CSV`);
    };
    reader.readAsText(file);
  };

  const handleBulkInvite = async () => {
    if (bulkImportData.length === 0) {
      toast.error('No users to import');
      return;
    }

    try {
      const response = await axios.post(`${API}/api/admin/company-users/bulk-invite`, {
        users: bulkImportData,
        default_role_id: inviteForm.role_id || null,
        default_team_id: inviteForm.team_id || null
      }, { headers });
      
      setBulkImportResults(response.data.results);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk invite');
    }
  };

  // Export Activity Logs
  const handleExportActivityLogs = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/activity-logs/export`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `activity_logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Activity logs exported');
    } catch (error) {
      toast.error('Failed to export activity logs');
    }
  };

  // 2FA Functions
  const fetch2FAStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/2fa/status`, { headers });
      setTwoFactorEnabled(response.data.enabled);
    } catch (error) {
      console.error('Failed to fetch 2FA status');
    }
  };

  const handleSetup2FA = async () => {
    try {
      const response = await axios.post(`${API}/api/admin/2fa/setup`, {}, { headers });
      setTwoFactorSetup(response.data);
      setShow2FAModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    }
  };

  const handleVerify2FA = async () => {
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    try {
      const res = await axios.post(`${API}/api/admin/2fa/verify`, { code: twoFactorCode }, { headers });
      setTimeout(() => setNewBackupCodes(res.data.backup_codes || null), 300);
      toast.success('Two-factor authentication enabled!');
      setShow2FAModal(false);
      setTwoFactorSetup(null);
      setTwoFactorCode('');
      setTwoFactorEnabled(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    }
  };

  const handleDisable2FA = async () => {
    if (!disable2FAForm.password || !(disable2FAForm.code.length === 6 || disable2FAForm.code.length >= 9)) {
      toast.error('Please enter your password and a 6-digit code or backup code');
      return;
    }
    try {
      await axios.post(`${API}/api/admin/2fa/disable`, { 
        password: disable2FAForm.password, 
        code: disable2FAForm.code 
      }, { headers });
      toast.success('Two-factor authentication disabled');
      setTwoFactorEnabled(false);
      setShowDisable2FAModal(false);
      setDisable2FAForm({ password: '', code: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    }
  };

  // Fetch 2FA status on mount
  useEffect(() => {
    if (token) {
      fetch2FAStatus();
    }
  }, [token]);

  // Create/Update role
  const handleSaveRole = async () => {
    if (!roleForm.name) {
      toast.error('Role name is required');
      return;
    }
    try {
      if (selectedRole) {
        await axios.put(`${API}/api/admin/roles/${selectedRole.id}`, roleForm, { headers });
        toast.success('Role updated');
      } else {
        await axios.post(`${API}/api/admin/roles`, roleForm, { headers });
        toast.success('Role created');
      }
      setShowRoleModal(false);
      setSelectedRole(null);
      setRoleForm({ name: '', description: '', permissions: {} });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  // Delete role
  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      await axios.delete(`${API}/api/admin/roles/${roleId}`, { headers });
      toast.success('Role deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  // Create/Update team
  const handleSaveTeam = async () => {
    if (!teamForm.name) {
      toast.error('Team name is required');
      return;
    }
    try {
      if (selectedTeam) {
        await axios.put(`${API}/api/admin/teams/${selectedTeam.id}`, teamForm, { headers });
        toast.success('Team updated');
      } else {
        await axios.post(`${API}/api/admin/teams`, teamForm, { headers });
        toast.success('Team created');
      }
      setShowTeamModal(false);
      setSelectedTeam(null);
      setTeamForm({ name: '', description: '', manager_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save team');
    }
  };

  // Delete team
  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try {
      await axios.delete(`${API}/api/admin/teams/${teamId}`, { headers });
      toast.success('Team deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    }
  };

  // Open role modal for editing
  const openRoleModal = (role = null) => {
    if (role) {
      setSelectedRole(role);
      setRoleForm({
        name: role.name,
        description: role.description || '',
        permissions: { ...role.permissions }
      });
    } else {
      setSelectedRole(null);
      // Initialize with default permissions
      const defaultPerms = {};
      permissionModules.forEach(m => defaultPerms[m.id] = 'none');
      setRoleForm({ name: '', description: '', permissions: defaultPerms });
    }
    setShowRoleModal(true);
  };

  // Open team modal for editing
  const openTeamModal = (team = null) => {
    if (team) {
      setSelectedTeam(team);
      setTeamForm({
        name: team.name,
        description: team.description || '',
        manager_id: team.manager_id || ''
      });
    } else {
      setSelectedTeam(null);
      setTeamForm({ name: '', description: '', manager_id: '' });
    }
    setShowTeamModal(true);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (user) => {
    if (user.invite_pending) {
      return <Badge className="bg-yellow-500 text-black">Pending</Badge>;
    }
    if (user.is_active) {
      return <Badge className="bg-green-600 text-white">Active</Badge>;
    }
    return <Badge className="bg-gray-500 text-white">Inactive</Badge>;
  };

  // Render Users sub-tab
  const renderUsersTab = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-[180px]" data-testid="filter-role-select">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {roles.map(role => (
              <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterTeam} onValueChange={setFilterTeam}>
          <SelectTrigger className="w-[180px]" data-testid="filter-team-select">
            <SelectValue placeholder="All Teams" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Teams</SelectItem>
            {teams.map(team => (
              <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[140px]" data-testid="filter-status-select">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <PermissionGate module="user_management" level="full">
        <Button onClick={() => setShowInviteModal(true)} data-testid="invite-user-btn">
          <UserPlus className="h-4 w-4 mr-2" />
          Invite User
        </Button>
        <Button variant="outline" onClick={() => setShowBulkImportModal(true)} data-testid="bulk-import-btn">
          <Upload className="h-4 w-4 mr-2" />
          Bulk Import
        </Button>
        </PermissionGate>
      </div>

      {/* Users List */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="text-left p-4 font-medium">User</th>
                  <th className="text-left p-4 font-medium">Role</th>
                  <th className="text-left p-4 font-medium">Team</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-left p-4 font-medium">Last Login</th>
                  <th className="text-right p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium">
                          {user.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      {user.role ? (
                        <Badge className={ROLE_COLORS[user.role.id] || 'bg-gray-500 text-white'}>
                          {user.role.name}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">No role</span>
                      )}
                      {user.is_trade && (
                        <Badge className="ml-1 bg-amber-100 text-amber-700 border border-amber-200">Customer</Badge>
                      )}
                    </td>
                    <td className="p-4">
                      {user.team ? (
                        <span className="flex items-center gap-1">
                          <UsersRound className="h-4 w-4 text-gray-400" />
                          {user.team.name}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-4">{getStatusBadge(user)}</td>
                    <td className="p-4 text-sm text-gray-500">
                      {user.last_login ? formatDate(user.last_login) : 'Never'}
                    </td>
                    <td className="p-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`user-row-menu-${user.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setSelectedUser(user);
                            setShowUserDetailModal(true);
                          }}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setSecurityUser(user)} data-testid={`user-security-menu-${user.id}`}>
                            <KeyRound className="h-4 w-4 mr-2" />
                            Password & Sessions
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {user.is_active && !user.invite_pending ? (
                            <DropdownMenuItem onClick={() => handleToggleUserStatus(user.id, false)}>
                              <UserX className="h-4 w-4 mr-2" />
                              Deactivate
                            </DropdownMenuItem>
                          ) : !user.invite_pending && (
                            <DropdownMenuItem onClick={() => handleToggleUserStatus(user.id, true)}>
                              <UserCheck className="h-4 w-4 mr-2" />
                              Activate
                            </DropdownMenuItem>
                          )}
                          {user.invite_pending && (
                            <DropdownMenuItem onClick={() => handleResendInvite(user.id, user.email)}>
                              <Mail className="h-4 w-4 mr-2" />
                              Resend Invite
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {user.invite_pending && !user.last_login ? (
                            <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteUser(user.id, user.name)} data-testid={`delete-invite-${user.id}`}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Invitation
                            </DropdownMenuItem>
                          ) : isSuperAdmin && user.id !== me?.id && (
                            <DropdownMenuItem className="text-red-600" onClick={() => setDeleteTarget(user)} data-testid={`delete-user-permanent-${user.id}`}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Permanently
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      {securityUser?.id === user.id && <UserSecurityDialog user={user} token={token} onClose={() => setSecurityUser(null)} />}
                      {deleteTarget?.id === user.id && <DeleteUserDialog user={user} token={token} onClose={() => setDeleteTarget(null)} onDeleted={() => { setDeleteTarget(null); fetchData(); }} />}
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render Roles sub-tab
  const renderRolesTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          System roles cannot be deleted. Create custom roles for specific needs.
        </p>
        <PermissionGate module="user_management" level="full">
        <Button onClick={() => openRoleModal()}>
          <Shield className="h-4 w-4 mr-2" />
          Create Role
        </Button>
        </PermissionGate>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {roles.map(role => (
          <Card key={role.id} className={role.is_system ? 'border-blue-200 dark:border-blue-800' : ''}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {role.name}
                    {role.is_system && (
                      <Badge variant="outline" className="text-xs">System</Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="text-sm mt-1">
                    {role.description || 'No description'}
                  </CardDescription>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openRoleModal(role)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit Permissions
                    </DropdownMenuItem>
                    {!role.is_system && (
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDeleteRole(role.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-500 mb-3">
                {role.user_count || 0} user{role.user_count !== 1 ? 's' : ''} assigned
              </div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(role.permissions || {}).slice(0, 6).map(([module, level]) => (
                  level !== 'none' && (
                    <Badge key={module} variant="outline" className="text-xs">
                      {module}: {level}
                    </Badge>
                  )
                ))}
                {Object.keys(role.permissions || {}).length > 6 && (
                  <Badge variant="outline" className="text-xs">
                    +{Object.keys(role.permissions).length - 6} more
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // Render Teams sub-tab
  const renderTeamsTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          Organize users into teams with assigned managers.
        </p>
        <PermissionGate module="user_management" level="full">
        <Button onClick={() => openTeamModal()}>
          <UsersRound className="h-4 w-4 mr-2" />
          Create Team
        </Button>
        </PermissionGate>
      </div>

      {teams.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <UsersRound className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No teams created yet</p>
            <PermissionGate module="user_management" level="full">
            <Button variant="outline" className="mt-4" onClick={() => openTeamModal()}>
              Create Your First Team
            </Button>
            </PermissionGate>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {teams.map(team => (
            <Card key={team.id}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{team.name}</CardTitle>
                    <CardDescription className="text-sm mt-1">
                      {team.description || 'No description'}
                    </CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openTeamModal(team)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDeleteTeam(team.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span>{team.member_count || 0} members</span>
                  </div>
                  {team.manager && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="h-4 w-4 text-gray-400" />
                      <span>Manager: {team.manager.name}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // Render Activity sub-tab
  const renderActivityTab = () => {
    const logs = Array.isArray(activityLogs?.logs) ? activityLogs.logs : 
                 Array.isArray(activityLogs) ? activityLogs : [];
    
    const getLogIcon = (type) => {
      switch(type) {
        case 'user_invited': return { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Invited User' };
        case 'user_deleted': return { bg: 'bg-red-100', text: 'text-red-600', label: 'Deleted User' };
        case 'first_login': return { bg: 'bg-green-100', text: 'text-green-600', label: 'First Login' };
        case 'login': return { bg: 'bg-purple-100', text: 'text-purple-600', label: 'Login' };
        case 'user_activated': return { bg: 'bg-emerald-100', text: 'text-emerald-600', label: 'Activated' };
        case 'user_deactivated': return { bg: 'bg-orange-100', text: 'text-orange-600', label: 'Deactivated' };
        default: return { bg: 'bg-gray-100', text: 'text-gray-600', label: type || 'Activity' };
      }
    };
    
    const formatLogDate = (dateStr) => {
      if (!dateStr) return '';
      try {
        return new Date(dateStr).toLocaleString();
      } catch {
        return dateStr;
      }
    };

    const getEmailTypeIcon = (type) => {
      switch(type) {
        case 'invitation': return { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Invitation' };
        case 'welcome': return { bg: 'bg-green-100', text: 'text-green-600', label: 'Welcome' };
        case 'reminder': return { bg: 'bg-amber-100', text: 'text-amber-600', label: 'Reminder' };
        case 'notification': return { bg: 'bg-purple-100', text: 'text-purple-600', label: 'Notification' };
        case 'schedule': return { bg: 'bg-cyan-100', text: 'text-cyan-600', label: 'Schedule' };
        default: return { bg: 'bg-gray-100', text: 'text-gray-600', label: type || 'Email' };
      }
    };
    
    return (
      <div className="space-y-4">
        {/* Toggle between Activity Logs, Email Logs, and Feedback */}
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant={activitySection === 'logs' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setActivitySection('logs')}
            data-testid="activity-logs-btn"
          >
            <Activity className="h-4 w-4 mr-2" />
            User Activity
          </Button>
          <Button 
            variant={activitySection === 'emails' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => {
              setActivitySection('emails');
              if (emailLogs.length === 0) fetchEmailLogs();
            }}
            data-testid="email-logs-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            Email Delivery
            {emailLogsSummary.failed > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 px-1.5">{emailLogsSummary.failed}</Badge>
            )}
          </Button>
          <Button 
            variant={activitySection === 'feedback' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => {
              setActivitySection('feedback');
              if (feedbackList.length === 0) fetchFeedback();
            }}
            data-testid="feedback-logs-btn"
          >
            <Star className="h-4 w-4 mr-2" />
            Customer Feedback
            {feedbackStats.pending > 0 && (
              <Badge className="ml-2 h-5 px-1.5 bg-amber-500">{feedbackStats.pending}</Badge>
            )}
          </Button>
        </div>

        {activitySection === 'logs' ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Activity Log
                  </CardTitle>
                  <CardDescription>Track user invitations, logins, and account changes</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={handleExportActivityLogs} data-testid="export-csv-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[600px]">
                <div className="divide-y">
                  {logs.map(log => {
                    const iconStyle = getLogIcon(log.type);
                    const detailsText = typeof log.details === 'string' ? log.details : 
                                        log.details ? JSON.stringify(log.details) : '';
                    return (
                      <div key={log.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-full ${iconStyle.bg} ${iconStyle.text}`}>
                            <Activity className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <div>
                                <Badge variant="outline" className="mr-2">{iconStyle.label}</Badge>
                                <span className="font-medium">{log.user_name || log.user_email || 'Unknown'}</span>
                              </div>
                              <span className="text-sm text-gray-400">
                                {formatLogDate(log.created_at)}
                              </span>
                            </div>
                            {detailsText && <p className="mt-1 text-sm text-gray-600">{detailsText}</p>}
                            {log.performed_by_email && log.performed_by !== log.user_id && (
                              <p className="mt-1 text-xs text-gray-400">
                                By: {log.performed_by_email}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {logs.length === 0 && (
                    <div className="p-8 text-center text-gray-500">
                      <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No activity logs yet</p>
                      <p className="text-sm mt-1">Activity will appear here when users are invited, login, or accounts are modified</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        ) : activitySection === 'emails' ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="h-5 w-5" />
                    Email Delivery Logs
                  </CardTitle>
                  <CardDescription>Track all system email notifications</CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Badge className="bg-green-600">{emailLogsSummary.successful}</Badge>
                    <span className="text-gray-500">sent</span>
                    {emailLogsSummary.failed > 0 && (
                      <>
                        <Badge variant="destructive">{emailLogsSummary.failed}</Badge>
                        <span className="text-gray-500">failed</span>
                      </>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchEmailLogs} disabled={emailLogsLoading}>
                    {emailLogsLoading ? 'Loading...' : 'Refresh'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Domain Verification Warning */}
              {emailLogsSummary.failed > 0 && (
                <div className="mx-4 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Email Delivery Issue</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Some emails are failing because your domain is not verified. To fix this:
                      </p>
                      <ol className="text-sm text-amber-700 mt-2 list-decimal list-inside space-y-1">
                        <li>Go to <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="underline font-medium">resend.com/domains</a></li>
                        <li>Add and verify your domain (apolloshades.com)</li>
                        <li>Once verified, emails will be delivered to any recipient</li>
                      </ol>
                    </div>
                  </div>
                </div>
              )}
              <ScrollArea className="h-[600px]">
                <div className="divide-y">
                  {emailLogs.map(email => {
                    const iconStyle = getEmailTypeIcon(email.email_type);
                    return (
                      <div key={email.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-full ${email.success ? 'bg-green-100' : 'bg-red-100'}`}>
                            {email.success ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <AlertCircle className="h-4 w-4 text-red-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <div>
                                <Badge variant="outline" className={`mr-2 ${iconStyle.bg} ${iconStyle.text} border-0`}>
                                  {iconStyle.label}
                                </Badge>
                                <span className="font-medium">{email.to_email}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-400">
                                  {formatLogDate(email.created_at)}
                                </span>
                                {!email.success && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    onClick={() => previewEmail(email.id)}
                                    data-testid={`preview-email-${email.id}`}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    View
                                  </Button>
                                )}
                                {!email.success && !email.retry_success && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    onClick={() => handleRetryEmail(email.id, email.to_email)}
                                    data-testid={`retry-email-${email.id}`}
                                  >
                                    <RefreshCw className="h-3 w-3 mr-1" />
                                    Retry
                                  </Button>
                                )}
                                {email.retry_success && (
                                  <Badge className="bg-blue-100 text-blue-600 text-xs">Retried</Badge>
                                )}
                              </div>
                            </div>
                            <p className="mt-1 text-sm text-gray-600 truncate max-w-xl">{email.subject}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                              <span>via {email.provider}</span>
                              {email.has_attachments && <span>• has attachments</span>}
                              {!email.success && email.error_message && (
                                <span className="text-red-500">• {email.error_message.substring(0, 60)}...</span>
                              )}
                              {email.retried_at && (
                                <span className="text-blue-500">• retried {formatLogDate(email.retried_at)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {emailLogs.length === 0 && !emailLogsLoading && (
                    <div className="p-8 text-center text-gray-500">
                      <Send className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No email logs yet</p>
                      <p className="text-sm mt-1">Email delivery logs will appear here when system emails are sent</p>
                    </div>
                  )}
                  {emailLogsLoading && (
                    <div className="p-8 text-center text-gray-500">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-3"></div>
                      <p>Loading email logs...</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        ) : activitySection === 'feedback' ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="h-5 w-5" />
                    Customer Feedback
                  </CardTitle>
                  <CardDescription>View customer ratings and comments from completed jobs</CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Badge className="bg-yellow-500">
                      <Star className="h-3 w-3 mr-1 fill-white" />
                      {feedbackStats.average_rating.toFixed(1)}
                    </Badge>
                    <span className="text-gray-500">avg rating</span>
                    <Badge className="bg-green-600">{feedbackStats.completed}</Badge>
                    <span className="text-gray-500">responses</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchFeedback} disabled={feedbackLoading}>
                    {feedbackLoading ? 'Loading...' : 'Refresh'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[600px]">
                <div className="divide-y">
                  {feedbackList.map(feedback => (
                    <div key={feedback.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full ${feedback.status === 'completed' ? 'bg-green-100' : 'bg-amber-100'}`}>
                          {feedback.status === 'completed' ? (
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          ) : (
                            <Clock className="h-4 w-4 text-amber-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-medium">{feedback.customer_name || 'Customer'}</span>
                              <span className="text-gray-400 ml-2">{feedback.customer_email}</span>
                            </div>
                            <span className="text-sm text-gray-400">
                              {feedback.submitted_at ? new Date(feedback.submitted_at).toLocaleString() : 
                               new Date(feedback.created_at).toLocaleString()}
                            </span>
                          </div>
                          {feedback.status === 'completed' && (
                            <div className="mt-2">
                              <div className="flex items-center gap-1 mb-1">
                                {[1, 2, 3, 4, 5].map(star => (
                                  <Star 
                                    key={star}
                                    className={`h-4 w-4 ${star <= feedback.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                                  />
                                ))}
                                <span className="ml-2 text-sm font-medium">{feedback.rating}/5</span>
                                {feedback.would_recommend === true && (
                                  <Badge className="ml-2 bg-green-100 text-green-700">Would recommend</Badge>
                                )}
                                {feedback.would_recommend === false && (
                                  <Badge className="ml-2 bg-red-100 text-red-700">Would not recommend</Badge>
                                )}
                              </div>
                              {feedback.comment && (
                                <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-2 rounded">
                                  "{feedback.comment}"
                                </p>
                              )}
                            </div>
                          )}
                          {feedback.status === 'pending' && (
                            <Badge className="mt-2 bg-amber-100 text-amber-700">Awaiting response</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {feedbackList.length === 0 && !feedbackLoading && (
                    <div className="p-8 text-center text-gray-500">
                      <Star className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No customer feedback yet</p>
                      <p className="text-sm mt-1">Feedback will appear here when customers respond to surveys after completed jobs</p>
                    </div>
                  )}
                  {feedbackLoading && (
                    <div className="p-8 text-center text-gray-500">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-3"></div>
                      <p>Loading feedback...</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">User Management</h2>
          <p className="text-gray-500">Manage company users, roles, teams and permissions</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} data-testid="user-management-tabs">
        <TabsList className="grid w-full grid-cols-5 max-w-2xl">
          <TabsTrigger value="users" className="flex items-center gap-2" data-testid="users-tab">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex items-center gap-2" data-testid="roles-tab">
            <Shield className="h-4 w-4" />
            Roles
          </TabsTrigger>
          <TabsTrigger value="teams" className="flex items-center gap-2" data-testid="teams-tab">
            <UsersRound className="h-4 w-4" />
            Teams
          </TabsTrigger>
          <TabsTrigger value="activity" className="flex items-center gap-2" data-testid="activity-tab">
            <Activity className="h-4 w-4" />
            Activity
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2" data-testid="security-tab">
            <ShieldCheck className="h-4 w-4" />
            Security
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          {loading ? <div className="text-center py-8">Loading...</div> : renderUsersTab()}
        </TabsContent>
        <TabsContent value="roles" className="mt-6">
          {loading ? <div className="text-center py-8">Loading...</div> : renderRolesTab()}
        </TabsContent>
        <TabsContent value="teams" className="mt-6">
          {loading ? <div className="text-center py-8">Loading...</div> : renderTeamsTab()}
        </TabsContent>
        <TabsContent value="activity" className="mt-6">
          {loading ? <div className="text-center py-8">Loading...</div> : renderActivityTab()}
        </TabsContent>
        <TabsContent value="security" className="mt-6">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5" />
                  Two-Factor Authentication
                </CardTitle>
                <CardDescription>Add an extra layer of security to your admin account</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">2FA Status</p>
                    <p className="text-sm text-gray-500">
                      {twoFactorEnabled ? 'Two-factor authentication is enabled' : 'Not enabled - your account is less secure'}
                    </p>
                  </div>
                  {twoFactorEnabled ? (
                    <Badge className="bg-green-600 text-white">Enabled</Badge>
                  ) : (
                    <Button onClick={handleSetup2FA} data-testid="enable-2fa-btn">
                      <QrCode className="h-4 w-4 mr-2" />
                      Enable 2FA
                    </Button>
                  )}
                </div>
                {twoFactorEnabled && (
                  <div className="mt-4 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      className="text-red-600"
                      data-testid="disable-2fa-btn"
                      onClick={() => setShowDisable2FAModal(true)}
                    >
                      Disable 2FA
                    </Button>
                    <BackupCodesPanel token={token} onCodes={setNewBackupCodes} />
                  </div>
                )}
                {newBackupCodes && <BackupCodesDialog codes={newBackupCodes} onClose={() => setNewBackupCodes(null)} />}
              </CardContent>
            </Card>

            {/* Push Notifications Card */}
            <PushNotificationSettings />
          </div>
        </TabsContent>
      </Tabs>

      {/* Invite User Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              Send an invitation email to add a new team member
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-name">Full Name *</Label>
              <Input
                id="invite-name"
                placeholder="John Smith"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email *</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="john@company.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role *</Label>
              <Select 
                value={inviteForm.role_id} 
                onValueChange={(val) => setInviteForm({...inviteForm, role_id: val})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-team">Team (Optional)</Label>
              <Select 
                value={inviteForm.team_id || 'none'} 
                onValueChange={(val) => setInviteForm({...inviteForm, team_id: val === 'none' ? '' : val})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Team</SelectItem>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>Cancel</Button>
            <Button onClick={handleInvite}>
              <Mail className="h-4 w-4 mr-2" />
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Modal */}
      <Dialog open={showRoleModal} onOpenChange={setShowRoleModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedRole ? 'Edit Role' : 'Create Role'}</DialogTitle>
            <DialogDescription>
              {selectedRole?.is_system 
                ? 'Edit permissions for this system role'
                : 'Define role name and permissions'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role-name">Role Name *</Label>
                <Input
                  id="role-name"
                  placeholder="Custom Role"
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({...roleForm, name: e.target.value})}
                  disabled={selectedRole?.is_system}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role-desc">Description</Label>
                <Input
                  id="role-desc"
                  placeholder="Role description"
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Permissions</Label>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="text-left p-3 font-medium">Module</th>
                      <th className="text-left p-3 font-medium">Access Level</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {permissionModules.map(module => (
                      <tr key={module.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="p-3">
                          <div className="font-medium">{module.name}</div>
                          <div className="text-xs text-gray-500">{module.description}</div>
                        </td>
                        <td className="p-3">
                          <Select
                            value={roleForm.permissions[module.id] || 'none'}
                            onValueChange={(val) => setRoleForm({
                              ...roleForm,
                              permissions: {...roleForm.permissions, [module.id]: val}
                            })}
                          >
                            <SelectTrigger className="w-[130px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {permissionLevels.map(level => (
                                <SelectItem key={level} value={level}>
                                  <Badge className={`${LEVEL_BADGES[level] || 'bg-gray-500'} text-xs`}>
                                    {level}
                                  </Badge>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleModal(false)}>Cancel</Button>
            <Button onClick={handleSaveRole}>
              {selectedRole ? 'Save Changes' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team Modal */}
      <Dialog open={showTeamModal} onOpenChange={setShowTeamModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedTeam ? 'Edit Team' : 'Create Team'}</DialogTitle>
            <DialogDescription>
              Organize users into teams for better management
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Team Name *</Label>
              <Input
                id="team-name"
                placeholder="Sales Team"
                value={teamForm.name}
                onChange={(e) => setTeamForm({...teamForm, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-desc">Description</Label>
              <Input
                id="team-desc"
                placeholder="Team description"
                value={teamForm.description}
                onChange={(e) => setTeamForm({...teamForm, description: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-manager">Team Manager</Label>
              <Select 
                value={teamForm.manager_id || 'none'} 
                onValueChange={(val) => setTeamForm({...teamForm, manager_id: val === 'none' ? '' : val})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Manager</SelectItem>
                  {users.filter(u => u.is_active).map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTeamModal(false)}>Cancel</Button>
            <Button onClick={handleSaveTeam}>
              {selectedTeam ? 'Save Changes' : 'Create Team'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Detail Modal */}
      <Dialog open={showUserDetailModal} onOpenChange={setShowUserDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-medium">
                  {selectedUser.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{selectedUser.name}</h3>
                  <p className="text-gray-500">{selectedUser.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <Label className="text-xs text-gray-500">Role</Label>
                  <Select 
                    value={selectedUser.role_id || 'none'}
                    onValueChange={(val) => handleUpdateUserRole(selectedUser.id, val === 'none' ? null : val, selectedUser.team_id)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Role</SelectItem>
                      {roles.map(role => (
                        <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Team</Label>
                  <Select 
                    value={selectedUser.team_id || 'none'}
                    onValueChange={(val) => handleUpdateUserRole(selectedUser.id, selectedUser.role_id, val === 'none' ? null : val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Team</SelectItem>
                      {teams.map(team => (
                        <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-gray-500">Status</Label>
                  <div className="mt-1">{getStatusBadge(selectedUser)}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Last Login</Label>
                  <div className="mt-1">{selectedUser.last_login ? formatDate(selectedUser.last_login) : 'Never'}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Created</Label>
                  <div className="mt-1">{formatDate(selectedUser.created_at)}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Invited By</Label>
                  <div className="mt-1">{selectedUser.invited_by || '-'}</div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDetailModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Modal */}
      <Dialog open={showBulkImportModal} onOpenChange={(open) => {
        setShowBulkImportModal(open);
        if (!open) {
          setBulkImportData([]);
          setBulkImportResults(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Bulk Import Users
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file with columns: name, email (one user per row)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!bulkImportResults ? (
              <>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVUpload}
                    className="hidden"
                    ref={fileInputRef}
                  />
                  <Upload className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag and drop a CSV file, or click to browse
                  </p>
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                    Choose CSV File
                  </Button>
                </div>
                
                {bulkImportData.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">{bulkImportData.length} users ready to import</p>
                      <Button variant="outline" size="sm" onClick={() => setBulkImportData([])}>
                        Clear
                      </Button>
                    </div>
                    <ScrollArea className="h-[200px] border rounded-lg">
                      <div className="p-2 space-y-1">
                        {bulkImportData.map((u, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded">
                            <span className="font-medium">{u.name}</span>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-600">{u.email}</span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Default Role</Label>
                        <Select 
                          value={inviteForm.role_id} 
                          onValueChange={(val) => setInviteForm({...inviteForm, role_id: val})}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select role..." />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.map(role => (
                              <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Default Team (optional)</Label>
                        <Select 
                          value={inviteForm.team_id} 
                          onValueChange={(val) => setInviteForm({...inviteForm, team_id: val})}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select team..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">No team</SelectItem>
                            {teams.map(team => (
                              <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                  <p className="font-medium">CSV Format Example:</p>
                  <code className="block mt-1 text-xs bg-blue-100 p-2 rounded">
                    name,email,role_id,team_id<br/>
                    John Smith,john@example.com,role_sales_rep,team_sales<br/>
                    Jane Doe,jane@example.com,,
                  </code>
                  <p className="text-xs mt-2 text-blue-600">
                    Note: role_id and team_id are optional. If omitted, users will use the default role/team selected above.
                  </p>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{bulkImportResults.success.length}</p>
                    <p className="text-sm text-green-700">Invited</p>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">{bulkImportResults.skipped.length}</p>
                    <p className="text-sm text-yellow-700">Skipped</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{bulkImportResults.failed.length}</p>
                    <p className="text-sm text-red-700">Failed</p>
                  </div>
                </div>
                
                {bulkImportResults.failed.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-red-600">Failed:</p>
                    {bulkImportResults.failed.map((f, i) => (
                      <div key={i} className="text-sm p-2 bg-red-50 rounded">
                        {f.email} - {f.reason}
                      </div>
                    ))}
                  </div>
                )}
                
                {bulkImportResults.skipped.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-yellow-600">Skipped (already exist):</p>
                    {bulkImportResults.skipped.map((s, i) => (
                      <div key={i} className="text-sm p-2 bg-yellow-50 rounded">
                        {s.email}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkImportModal(false)}>
              {bulkImportResults ? 'Close' : 'Cancel'}
            </Button>
            {!bulkImportResults && bulkImportData.length > 0 && (
              <Button onClick={handleBulkInvite}>
                Send {bulkImportData.length} Invitations
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2FA Setup Modal */}
      <Dialog open={show2FAModal} onOpenChange={setShow2FAModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Setup Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </DialogDescription>
          </DialogHeader>
          {twoFactorSetup && (
            <div className="space-y-4 py-4">
              <div className="flex justify-center">
                <div className="p-4 bg-white border rounded-lg">
                  <img 
                    src={twoFactorSetup.qr_image}
                    data-testid="mfa-setup-qr"
                    alt="2FA QR Code"
                    className="w-48 h-48"
                  />
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">Or enter this code manually:</p>
                <code className="px-3 py-2 bg-gray-100 rounded font-mono text-sm">
                  {twoFactorSetup.secret}
                </code>
              </div>
              
              <div className="space-y-2">
                <Label>Enter the 6-digit code from your app</Label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-widest"
                  data-testid="mfa-setup-code-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShow2FAModal(false);
              setTwoFactorSetup(null);
              setTwoFactorCode('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleVerify2FA} disabled={twoFactorCode.length !== 6} data-testid="mfa-setup-verify-btn">
              Verify & Enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Confirmation Modal */}
      <Dialog open={showDisable2FAModal} onOpenChange={(open) => {
        setShowDisable2FAModal(open);
        if (!open) setDisable2FAForm({ password: '', code: '' });
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldCheck className="h-5 w-5" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Enter your password and current 2FA code to disable two-factor authentication.
              This will make your account less secure.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="disable-2fa-password">Password</Label>
              <Input
                id="disable-2fa-password"
                type="password"
                placeholder="Enter your password"
                value={disable2FAForm.password}
                onChange={(e) => setDisable2FAForm({...disable2FAForm, password: e.target.value})}
                data-testid="disable-2fa-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="disable-2fa-code">Current 2FA Code</Label>
              <Input
                id="disable-2fa-code"
                placeholder="6-digit code or backup code"
                maxLength={9}
                value={disable2FAForm.code}
                onChange={(e) => setDisable2FAForm({...disable2FAForm, code: e.target.value.replace(/[^0-9a-fA-F-]/g, '')})}
                className="text-center text-2xl tracking-widest"
                data-testid="disable-2fa-code-input"
              />
              <p className="text-xs text-gray-500">Enter the 6-digit code from your authenticator app</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDisable2FAModal(false);
              setDisable2FAForm({ password: '', code: '' });
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDisable2FA}
              disabled={!disable2FAForm.password || disable2FAForm.code.length !== 6}
              data-testid="confirm-disable-2fa-btn"
            >
              Disable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Preview Modal */}
      <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          {emailPreviewContent && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">To:</span>
                  <span className="ml-2 font-medium">{emailPreviewContent.to_email}</span>
                </div>
                <div>
                  <span className="text-gray-500">Type:</span>
                  <span className="ml-2">{emailPreviewContent.email_type}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Subject:</span>
                  <span className="ml-2 font-medium">{emailPreviewContent.subject}</span>
                </div>
                {emailPreviewContent.error_message && (
                  <div className="col-span-2 p-3 bg-red-50 rounded-lg text-red-700 text-sm">
                    <strong>Error:</strong> {emailPreviewContent.error_message}
                  </div>
                )}
              </div>
              <div className="border-t pt-4">
                <p className="text-sm text-gray-500 mb-2">Email Content:</p>
                {emailPreviewContent.html_content ? (
                  <div 
                    className="border rounded-lg p-4 bg-white max-h-[400px] overflow-auto"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(emailPreviewContent.html_content) }}
                  />
                ) : (
                  <p className="text-gray-400 italic">Content not available</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
